import { InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { atom, useAtom } from "jotai";
import { useNavigate } from "react-router-dom";

import { get } from "api";
import { GetResult } from "api/types";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { EmptyState } from "molecules/empty-state/EmptyState";
import { HistoryListEntryProps } from "molecules/history-list-entry/HistoryListEntry";
import TopNav from "molecules/navbars/TopNav";
import { HistoryList } from "organisms/history-list/HistoryList";
import { notNull } from "toolbox/Guards";
import { formatCurrency } from "toolbox/Money";

const countAtom = atom(1);

const formatHistoryData = (
  data: InfiniteData<GetResult<"/user/orders">>,
  onClick: (id: string) => void
) => {
  // Create a single reusable callback function that takes an id parameter
  const handleClick = (id: string) => () => onClick(id);

  const values =
    data.pages?.flatMap((page) => page.items).filter(notNull) ?? [];
  const items = values.reduce((rewards, item) => {
    const date: string = dayjs(item.date).format("YYYY-MM");

    if (!(date in rewards)) {
      rewards[date] = [];
    }

    rewards[date].push({
      id: item.id,
      title: `Order: ${item.orderNumber}`,
      date: item.date,
      subtitle: `${formatCurrency(item.amount)}`,
      refunded: item.isRefunded,
      onClick: handleClick(item.id),
    });
    return rewards;
  }, {} as Record<string, HistoryListEntryProps[]>);

  const groupArrays = Object.keys(items).map((date) => {
    return {
      date,
      items: items[date],
    };
  });

  return groupArrays;
};

export const OrderHistoryPage = () => {
  const navigate = useNavigate();
  const [count, setCount] = useAtom(countAtom);

  const { fetchNextPage, data, hasNextPage } = useInfiniteQuery({
    queryKey: ["/user/orders"],
    queryFn: ({ pageParam = 1 }) => get("/user/orders", { page: pageParam }),
    getNextPageParam: ({ page, totalPages }) =>
      page < totalPages ? page + 1 : undefined,
    onSuccess: (data) => {
      if (data.pages.length < count) {
        fetchNextPage();
      }
    },
  });

  if (!data) return null;

  const isDataAvailable = data.pages[0].count > 0;

  return (
    <div className="relative flex h-full flex-col bg-one">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">ORDER HISTORY</div>
      </TopNav>

      <div className="flex flex-col text-center">
        {!isDataAvailable && (
          <EmptyState
            title="You haven’t made any purchases yet"
            body="Browse the shop to buy your first treatment and start your journey!"
            buttonText="Browse shop"
            onClick={() => navigate("/shop")}
          />
        )}
        <HistoryList
          historyListItems={formatHistoryData(data, (id) =>
            navigate(`/account/orders/${id}`)
          )}
          showLoadMoreResultsButton={hasNextPage}
          onLoadMoreClick={() => {
            fetchNextPage();
            setCount(data.pages.length + 1);
          }}
        />
      </div>
      <div className="grow" />
      <div className="flex w-full scroll-py-4 flex-col items-center">
        <RepeatMDFooter />
      </div>
    </div>
  );
};
